import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, FolderOpen, Wrench, GitBranch, Droplet, Activity, Gauge } from "lucide-react";
import { PROFILE_KINDS, PROFILE_KIND_LABELS, PROFILE_KIND_HELPER } from "@/lib/profiles/schemas";
import { listProfiles } from "@/lib/profiles/storage";

// Profile Hub — landing page for the new Profile Manager (P1 of Kill Sheet
// Redev). Lists the 5 profile kinds with counts and quick-launch links.

const ICONS = {
  well:    FolderOpen,
  bha:     Wrench,
  casing:  GitBranch,
  mud:     Droplet,
  pump:    Activity,
  opstate: Gauge,
};

function ProfileCard({ kind, count }) {
  const Icon = ICONS[kind] || FolderOpen;
  return (
    <Link
      to={`/profiles/${kind}`}
      data-testid={`profile-hub-card-${kind}`}
      className="card-rs flex items-start gap-3 p-4 hover:border-[var(--rs-amber,#f5b400)] transition-colors"
      style={{ minHeight: 96 }}
    >
      <div
        className="flex items-center justify-center rounded-md"
        style={{
          width: 40, height: 40, flexShrink: 0,
          background: "rgba(245,180,0,0.10)",
          color: "var(--rs-amber, #f5b400)",
          border: "1px solid rgba(245,180,0,0.25)",
        }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-[14px] font-semibold text-[var(--rs-text-1)]">
            {PROFILE_KIND_LABELS[kind]}
          </h3>
          <span
            data-testid={`profile-hub-count-${kind}`}
            className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)]"
          >
            {count} saved
          </span>
        </div>
        <p className="text-[11px] text-[var(--rs-text-3)] leading-snug mt-1">
          {PROFILE_KIND_HELPER[kind]}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] mt-1.5" />
    </Link>
  );
}

export default function ProfileHub() {
  const counts = {};
  for (const k of PROFILE_KINDS) {
    try { counts[k] = listProfiles(k).length; }
    catch { counts[k] = 0; }
  }
  return (
    <div data-testid="profile-hub" className="max-w-3xl mx-auto px-4 py-6">
      <header className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)] mb-1">
          Foundation · Kill Sheet System
        </div>
        <h1 className="text-[22px] font-semibold tracking-tight text-[var(--rs-text-1)]">
          Profile Manager
        </h1>
        <p className="text-[12px] text-[var(--rs-text-3)] leading-snug mt-1">
          Save, version, and reuse your rig configuration. Profiles created here
          are consumed by the new IWCF Kill Sheet wizard and are{" "}
          <strong className="text-[var(--rs-text-2)]">snapshot-locked</strong>{" "}
          into every kill sheet at the moment it is generated — later edits do
          not retroactively change historical sheets.
        </p>
      </header>

      {/* RC-1 — Deprecation banner. The Profile Manager has been superseded
          by the RigSentinel Context Engine. Existing data is read-only; new
          engineering data should be entered through Well Profile, Rig Data
          Sheet, BHA, Drill String, and Mud Program editors instead. */}
      <div
        data-testid="profile-hub-deprecated-banner"
        className="card-rs p-3 mb-5"
        style={{
          background:  "rgba(244,67,54,0.06)",
          borderColor: "#C62828",
          borderLeft:  "2px solid #C62828",
        }}
      >
        <div className="text-[10px] tracking-[0.18em] uppercase font-semibold mb-1 text-[#FCA5A5]">
          Deprecated · Read-only
        </div>
        <p className="text-[11.5px] text-[var(--rs-text-2)] leading-snug">
          The Profile Manager is being retired and replaced by the
          RigSentinel Context Engine. Use the{" "}
          <Link to="/wells" className="text-[var(--rs-amber)] underline">
            Well Profile
          </Link>
          ,{" "}
          <Link to="/rig-sheets" className="text-[var(--rs-amber)] underline">
            Rig Data Sheet
          </Link>
          , BHA, Drill String, and Mud Program editors instead. Profile
          values are still readable, but new engineering data must be
          entered through the canonical editors above. This page will be
          removed in V2.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PROFILE_KINDS.map((k) => (
          <ProfileCard key={k} kind={k} count={counts[k]} />
        ))}
      </div>
      <p
        data-testid="profile-hub-footnote"
        className="text-[10.5px] text-[var(--rs-text-3)] mt-6 leading-snug"
      >
        Profile editing is free for all RigSentinel users. The Kill Sheet
        Library, historical Kill Sheet storage, and IWCF-style PDF export
        require RigSentinel Pro.
      </p>
    </div>
  );
}
